// kidSfx — Web Audio synthesized sound effects for the kid-mode
// surfaces. All clips are oscillator-built so we ship zero new audio
// assets (the bundle was already gaining MB from cwebp re-encoding;
// adding mp3s would push it further).
//
// Why synthesis and not the existing sfx.js mp3 library? Two reasons:
//   1. Tonal vocabulary — the existing SFX are mastered for the
//      adult cooking UI (button-press, kitchen-tab, paper-flipping).
//      Kids deserve sparkle / coin / munch / fanfare semantics that
//      don't exist in that file pile.
//   2. Bundle weight — every new mp3 is 30-100KB. A library of 8
//      synthesized clips costs zero bytes; we draw the waveform per
//      call from oscillators + envelopes.
//
// All clips are short (<800ms). Each call to playKidSfx() opens one
// or more OscillatorNode → GainNode chains, schedules them once,
// then lets the audio graph garbage-collect itself when stopped.
// No re-trigger cost because the buffers don't exist — oscillators
// are cheap to spin up at call-time.
//
// Why not BoodaVoiceEngine? That path is gated on voice-command
// engine.isRunning. Kid mode doesn't run voice commands; routing
// through the engine would mean the SFX silently no-op unless voice
// happens to be active. Web Audio is the right path here.
//
// All public functions are noops on iOS WKWebView when AudioContext
// is suspended (no user gesture yet) — we never throw on missing
// context; the first user tap unlocks it for subsequent plays.
//
// Opt-out: localStorage["mise.kidSfx.enabled"] = "false" silences
// kid SFX. Default on (kids notice missing feedback before they
// notice extra feedback).

const AudioCtxCtor =
  typeof window !== "undefined"
    ? window.AudioContext || window.webkitAudioContext
    : null;

const MASTER_GAIN = 0.45;

let ctx = null;
let masterGain = null;

function audioEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("mise.kidSfx.enabled") !== "false";
  } catch {
    return true;
  }
}

function ensureCtx() {
  if (!AudioCtxCtor) return null;
  if (!ctx) {
    try {
      ctx = new AudioCtxCtor();
      masterGain = ctx.createGain();
      masterGain.gain.value = MASTER_GAIN;
      masterGain.connect(ctx.destination);
    } catch {
      return null;
    }
  }
  // Some iOS versions ship the ctx in "suspended" state until a user
  // gesture. Resume on every call — no-op when already running.
  if (ctx.state === "suspended") {
    try { ctx.resume(); } catch { /* noop */ }
  }
  return ctx;
}

// Helper: schedule a tone with an attack/decay envelope. Single
// OscillatorNode + GainNode pair, started at `startAt` (audio-clock
// seconds), stopped at startAt + duration + 0.05 tail.
//
// type: "sine" | "triangle" | "square" | "sawtooth"
function tone({ freq, type = "sine", startAt, duration, peak = 0.5, attack = 0.005, decay }) {
  if (!ctx || !masterGain) return;
  const dec = typeof decay === "number" ? decay : duration;
  const osc = ctx.createOscillator();
  const g   = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(peak, startAt + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + attack + dec);
  osc.connect(g).connect(masterGain);
  osc.start(startAt);
  osc.stop(startAt + attack + dec + 0.04);
}

// Two-osc tone: detuned pair gives a richer "metallic" texture for
// coin / chime sounds without resorting to FM synthesis.
function dualTone({ freq, type = "triangle", startAt, duration, peak = 0.4, attack = 0.005, decay, detuneCents = 6 }) {
  if (!ctx) return;
  tone({ freq,                              type, startAt, duration, peak, attack, decay });
  tone({ freq: freq * (1 + detuneCents / 1200), type, startAt, duration, peak: peak * 0.85, attack, decay });
}

// Quick filtered-noise burst — for percussive thunks / munches. Uses
// a one-shot AudioBufferSourceNode with white noise + a low-pass
// filter for warmth.
function noiseBurst({ startAt, duration = 0.08, peak = 0.35, cutoff = 800, q = 1.2 }) {
  if (!ctx || !masterGain) return;
  const sampleRate = ctx.sampleRate;
  const frames = Math.max(1, Math.round(duration * sampleRate));
  const buf = ctx.createBuffer(1, frames, sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = cutoff;
  filter.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(peak, startAt + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  src.connect(filter).connect(g).connect(masterGain);
  src.start(startAt);
  src.stop(startAt + duration + 0.04);
}

// ─── Public clips ────────────────────────────────────────────────

// sparkle — fast ascending arpeggio of high triangle tones, bell-
// decay. For card reveal flash, magic-pen activation, treats unlock.
function sparkle() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  const notes = [1046.5, 1318.5, 1568.0, 2093.0]; // C6 E6 G6 C7
  notes.forEach((f, i) => {
    dualTone({ freq: f, type: "triangle", startAt: t + i * 0.06, duration: 0.32, peak: 0.32, decay: 0.35 });
  });
}

// coin — two-tone ascending ding with a metallic third. For card
// collect after reveal, item collect, achievement small.
function coin() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  dualTone({ freq: 783.99, type: "triangle", startAt: t,        duration: 0.10, peak: 0.5, decay: 0.12 });  // G5
  dualTone({ freq: 1046.5, type: "triangle", startAt: t + 0.08, duration: 0.26, peak: 0.5, decay: 0.30 });  // C6
}

// munch — low filtered-noise thump then a quick second chomp. For
// card spent on pet, pet feed.
function munch() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  noiseBurst({ startAt: t,        duration: 0.09, peak: 0.42, cutoff: 640, q: 1.4 });
  noiseBurst({ startAt: t + 0.11, duration: 0.07, peak: 0.32, cutoff: 540, q: 1.2 });
  tone({ freq: 180, type: "sine", startAt: t, duration: 0.06, peak: 0.18, decay: 0.07 });
}

// fanfare — major C arpeggio C5 E5 G5 C6 with each note overlapping
// the next so it reads as a triumphant chord build. For level up.
function fanfare() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  // C5, E5, G5, C6 — root, third, fifth, octave.
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((f, i) => {
    dualTone({
      freq: f, type: "triangle",
      startAt: t + i * 0.09,
      duration: 0.55,
      peak: 0.4,
      decay: 0.65,
    });
  });
  // Hold-out top C for a little longer.
  dualTone({ freq: 1046.5, type: "triangle", startAt: t + 0.45, duration: 0.7, peak: 0.32, decay: 0.85 });
}

// thunk — quick low sine for generic tile / button feedback when
// nothing more specific applies.
function thunk() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  tone({ freq: 220, type: "sine", startAt: t, duration: 0.06, peak: 0.35, decay: 0.07 });
}

// trill — quick triplet of ascending pings. For "I WANT THIS!"
// successfully sent. Reads as "request away!"
function trill() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  const notes = [659.25, 783.99, 987.77]; // E5 G5 B5
  notes.forEach((f, i) => {
    tone({ freq: f, type: "triangle", startAt: t + i * 0.05, duration: 0.10, peak: 0.4, decay: 0.12 });
  });
}

// tick — soft 80ms tick for segment switches. Quiet on purpose
// (high-frequency taps would be irritating loud).
function tick() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  tone({ freq: 1568, type: "sine", startAt: t, duration: 0.05, peak: 0.18, decay: 0.06 });
}

// pop — rising chirp for a positive small action (toggle on, etc.)
function pop() {
  if (!ensureCtx() || !audioEnabled()) return;
  const t = ctx.currentTime;
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(440, t);
  osc.frequency.exponentialRampToValueAtTime(880, t + 0.08);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.35, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
  osc.connect(g).connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.16);
}

const CLIPS = { sparkle, coin, munch, fanfare, thunk, trill, tick, pop };

export function playKidSfx(name) {
  const fn = CLIPS[name];
  if (!fn) {
    console.warn("[kidSfx] unknown clip:", name);
    return;
  }
  try { fn(); } catch (e) { console.warn("[kidSfx]", name, "failed:", e?.message || e); }
}

export function setKidSfxEnabled(enabled) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem("mise.kidSfx.enabled", enabled ? "true" : "false");
  } catch { /* quota */ }
}

export function isKidSfxEnabled() {
  return audioEnabled();
}
